import { CloseCircleOutline, Currency } from '@aslanpay/portal-common-lib';
import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { getCurrencySymbolForCode } from 'app/utils/currency';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';

export interface MoneyAmountInputProps {
  currency?: Currency;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: string;
  maxDecimalPlaces?: number;
  textAlign?: 'left' | 'right';
  fontFamily?: string;
  clearAriaLabel?: string;
}

function MoneyAmountInput({
  currency = 'GBP',
  value,
  onChange,
  onBlur = undefined,
  placeholder = 'Enter amount',
  isDisabled = false,
  isInvalid = false,
  size = 'lg',
  maxDecimalPlaces = 2,
  textAlign = 'left',
  fontFamily = undefined,
  clearAriaLabel = 'Clear amount',
}: MoneyAmountInputProps) {
  const [displayValue, setDisplayValue] = useState<string>('');

  /**
   * Format the unformatted value for display
   * Adds thousand separators and ensures proper decimal formatting
   */
  const formatForDisplay = useCallback((unformattedValue: string): string => {
    if (!unformattedValue) {
      return '';
    }

    // Extract only digits and single decimal point
    const sanitized = unformattedValue.replace(/[^\d.]/g, '');

    if (!sanitized) {
      return '';
    }

    // Split by decimal point
    const parts = sanitized.split('.');
    const integerPart = parts[0] || '0';
    const decimalPart = parts[1] !== undefined ? parts[1] : '';

    // Add thousand separators to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Combine with decimal part if it exists (including trailing decimal)
    if (decimalPart || unformattedValue.endsWith('.')) {
      return `${formattedInteger}.${decimalPart}`;
    }

    return formattedInteger;
  }, []);

  /**
   * Get the currency symbol for display
   */
  const getCurrencySymbol = useCallback((): string => {
    return getCurrencySymbolForCode(currency);
  }, [currency]);

  /**
   * Handle input change event
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;

    // Remove any non-numeric characters except decimal point (unless maxDecimalPlaces is 0)
    if (maxDecimalPlaces === 0) {
      inputValue = inputValue.replace(/[^\d]/g, '');
    } else {
      inputValue = inputValue.replace(/[^\d.]/g, '');
    }

    // If maxDecimalPlaces is 0, don't allow decimal point at all
    if (maxDecimalPlaces === 0) {
      inputValue = inputValue.replace(/\./g, '');
    }

    // Ensure only one decimal point
    let parts = inputValue.split('.');
    if (parts.length > 2) {
      inputValue = `${parts[0]}.${parts.slice(1).join('')}`;
      parts = inputValue.split('.');
    }

    // Limit decimal places
    if (parts[1]) {
      if (parts[1].length > maxDecimalPlaces) {
        inputValue = `${parts[0]}.${parts[1].substring(0, maxDecimalPlaces)}`;
      }
    }

    // Remove leading zeros (but keep single 0 before decimal)
    if (inputValue.length > 1 && inputValue[0] === '0' && inputValue[1] !== '.') {
      inputValue = inputValue.substring(1);
    }

    // Update the raw value via onChange callback
    onChange(inputValue);

    // Update display value with formatting
    const formatted = formatForDisplay(inputValue);
    setDisplayValue(formatted);
  };

  /**
   * Handle blur event
   */
  const handleBlur = () => {
    onBlur?.();
  };

  /**
   * Handle clear button click
   */
  const handleClear = () => {
    onChange('');
    setDisplayValue('');
  };

  // Sync display value when the external value prop changes
  useEffect(() => {
    const formatted = formatForDisplay(value);
    setDisplayValue(formatted);
  }, [value, formatForDisplay]);

  return (
    <InputGroup size={size}>
      {displayValue && (
        <InputLeftElement
          pointerEvents="none"
          height="full"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          pr="2"
        >
          <Text userSelect="none" display="flex" alignItems="center" height="full">
            {getCurrencySymbol()}
          </Text>
        </InputLeftElement>
      )}
      <Input
        pl={displayValue ? 10 : 4}
        pr={displayValue && !isDisabled ? 9 : 4}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        textAlign={textAlign}
        fontFamily={fontFamily}
        bg="white"
        _placeholder={{ color: 'brand.mediumGray2' }}
        errorBorderColor="system.error"
        _focus={isInvalid ? { borderColor: 'system.error', boxShadow: 'none' } : undefined}
      />
      {displayValue && !isDisabled && (
        <InputRightElement height="full">
          <IconButton
            type="button"
            size="xs"
            variant="ghost"
            aria-label={clearAriaLabel}
            icon={<CloseCircleOutline boxSize="16px" />}
            onClick={handleClear}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
}

export default MoneyAmountInput;
